import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { QuoteHistory } from '../../services/types';
import { parseCalendarDate } from '../../utils/calendarDate';

interface PartQuoteHistoryTableProps {
  quoteHistory: QuoteHistory[];
}

export const PartQuoteHistoryTable: React.FC<PartQuoteHistoryTableProps> = ({
  quoteHistory,
}) => {
  const { t } = useTranslation();

  const totalQty = quoteHistory.reduce(
    (sum, record) => sum + record.expectedQty,
    0
  );

  return (
    <div className="bg-white rounded border border-gray-300 shadow-sm flex flex-col">
      {/* Header */}
      <div className="bg-indigo-50 px-4 py-3 border-b border-indigo-200 flex-shrink-0">
        <h3 className="text-lg font-bold text-indigo-900">
          {t('parts.quoteHistory')}
        </h3>
        <p className="text-xs text-indigo-700">
          {quoteHistory.length}{' '}
          {quoteHistory.length === 1
            ? t('parts.records')
            : t('parts.recordPlural')}
          {totalQty !== 0 && ` • Total: ${totalQty.toLocaleString()}`}
        </p>
      </div>

      {/* Content */}
      {quoteHistory.length > 0 ? (
        <div className="overflow-x-auto flex-grow">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200 sticky top-0">
                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  {t('parts.quoteHistoryTable.date')}
                </th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  {t('parts.quoteHistoryTable.qty')}
                </th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  {t('parts.quoteHistoryTable.unitPrice')}
                </th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">
                  {t('parts.quoteHistoryTable.currency')}
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  {t('parts.quoteHistoryTable.quoteNum')}
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  {t('parts.quoteHistoryTable.shipTo')}
                </th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">
                  {t('parts.quoteHistoryTable.ordered')}
                </th>
              </tr>
            </thead>
            <tbody>
              {quoteHistory.map((record, idx) => (
                <tr key={idx} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-700 font-medium whitespace-nowrap">
                    {formatDate(record.dateQuoted)}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-900 font-semibold">
                    {record.expectedQty.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-900 font-medium whitespace-nowrap">
                    ${record.netUnitPrice.toFixed(2)}
                    {record.discountPercent > 0 && (
                      <span className="ml-1 text-xs text-gray-500">
                        ({record.discountPercent}% DSC)
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center text-gray-700 font-medium">
                    {record.currencyCode}
                  </td>
                  <td className="px-4 py-3 font-medium whitespace-nowrap">
                    <Link
                      to={`/quotes/${record.quoteNum}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {record.quoteNum}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {record.shipToName}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${
                        record.ordered
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {record.ordered ? 'Yes' : 'No'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="pb-2">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-tight mb-1">
                Date
              </p>
              <p className="text-sm font-medium text-gray-900">-</p>
            </div>
            <div className="pb-2 text-right">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-tight mb-1">
                Qty
              </p>
              <p className="text-sm font-medium text-gray-900">0</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Format date string for display
 * Handles ISO format, timestamps, and various date formats
 */
function formatDate(dateString: string): string {
  if (!dateString) return '-';

  try {
    let date: Date;

    // Try parsing as ISO date first
    if (dateString.includes('T') || dateString.includes('-')) {
      date = parseCalendarDate(dateString);
    } else if (/^\d+$/.test(dateString)) {
      // Handle timestamp in milliseconds
      date = new Date(parseInt(dateString, 10));
    } else {
      // Try direct Date constructor
      date = new Date(dateString);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString; // Return original if parsing failed
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
}
